
import React from "react";
import { useState, useEffect } from 'react';
import {  TrendingUp, BarChart3, DollarSign } from 'lucide-react';
import { calculateFinancialRatios, calculateLLPFinancialRatios } from "../utils/financialRatios"; // adjust path based on your folder structure

interface FinancialRatiosProps {
  className?: string;
  onClose?: () => void;
  financialData?: any;
  isLLPIN?: boolean;
}

export const FinancialRatios: React.FC<FinancialRatiosProps> = ({ className, onClose, financialData, isLLPIN }) => {
  // const [financialData, setFinancialData] = useState([]);
  const [ratios, setRatios] = useState<any>([]);


  useEffect(() => { 
    let data = isLLPIN ? calculateLLPFinancialRatios(financialData) : calculateFinancialRatios(financialData);
    setRatios(data);
  }, [isLLPIN, financialData]);

  const formatNumber = (num:any, decimals = 2) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    return Number(num).toFixed(decimals);
  };

  const formatCurrency = (num:any) => {
    if (num === null || num === undefined || isNaN(num)) return 'N/A';
    return `₹${Number(num).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        {ratios.length > 0 && (
          <div className="grid gap-8">
            {/* Revenue & Profitability */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <TrendingUp className="h-6 w-6 text-green-600" />
                <h2 className="text-2xl font-bold text-gray-800">Revenue & Profitability</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left font-semibold">Metric</th>
                      {ratios.map((ratio:any) => (
                        <th key={ratio.year} className="border p-3 text-center font-semibold">
                          {ratio.year}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-3 font-medium">Net Revenue</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.netRevenue)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Revenue Growth %</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.revenueGrowth)}%</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Other Income</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.otherIncome)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">COGS</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.cogs)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Employee Benefit Expenses</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.employeeBenefitExpenses)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Other Expenses</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.otherExpenses)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">EBITDA</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.ebitda)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">EBITDA %</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.ebitdaPercent)}%</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Finance Cost</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.financeCost)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Depreciation</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.depreciation)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">PAT</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.pat)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">PAT %</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.patPercent)}%</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Assets & Liabilities */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <BarChart3 className="h-6 w-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-800">Assets & Capital Structure</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left font-semibold">Metric</th>
                      {ratios.map((ratio:any) => (
                        <th key={ratio.year} className="border p-3 text-center font-semibold">
                          {ratio.year}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-3 font-medium">Tangible Assets</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.tangibleAssets)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Intangible Assets</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.intangibleAssets)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Capital WIP</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.capitalWIP)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Non Current Assets (Excl Fixed Assets)</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.nonCurrentAssetsExclFixed)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Tangible Net Worth (TNW)</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.tangibleNetWorth)}</td>
                      ))}
                    </tr>
                    {/* <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Adjusted Tangible Net Worth</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.adjustedTangibleNetWorth)}</td>
                      ))}
                    </tr> */}
                    <tr>
                      <td className="border p-3 font-medium">Total Outside Liabilities (TOL)</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.totalOutsideLiabilities)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">TOL/TNW</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.tolTnwRatio)}</td>
                      ))}
                    </tr>
                    {/* <tr>
                      <td className="border p-3 font-medium">TOL/ATNW</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.tolAtnwRatio)}</td>
                      ))}
                    </tr> */}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Liquidity & Working Capital */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="h-6 w-6 text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-800">Liquidity & Working Capital</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border p-3 text-left font-semibold">Metric</th>
                      {ratios.map((ratio:any) => (
                        <th key={ratio.year} className="border p-3 text-center font-semibold">
                          {ratio.year}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border p-3 font-medium">Long Term Debt</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.longTermDebt)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Short Term Debt</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.shortTermDebt)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Total Debt</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.totalDebt)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Current Assets</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.currentAssets)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Current Liabilities</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.currentLiabilities)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Current Ratio</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.currentRatio)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Net Working Capital</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.netWorkingCapital)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Inventories</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.inventories)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Trade Receivables</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.tradeReceivables)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Trade Payables</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.tradePayables)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Cash & Cash Equivalents</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.cashAndCashEquivalents)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Inventories Holding Period (Days)</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.inventoriesHoldingPeriod, 0)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Debtors Holding Period (Days)</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.debtorsHoldingPeriod, 0)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Creditors Holding Period (Days)</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.creditorsHoldingPeriod, 0)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">Drawing Power</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatCurrency(ratio.drawingPower)}</td>
                      ))}
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border p-3 font-medium">Debt Equity Ratio</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.debtEquityRatio)}</td>
                      ))}
                    </tr>
                    <tr>
                      <td className="border p-3 font-medium">DSCR</td>
                      {ratios.map((ratio:any) => (
                        <td key={ratio.year} className="border p-3 text-center">{formatNumber(ratio.dscr)}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


